// @flow
import { ZmCollection } from '../../autogenerated/ZoomBridge';
import './CollectionsTreeView.css';

import * as React from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import KeyboardShortcuts from '../../keyboard-shortcuts/KeyboardShortcuts';
import KeydownConfig from '../../keyboard-shortcuts/KeydownConfig';
import { KeyCode, SUBTAB } from '../../util/Constants';
import ExpandButton from '../../common/ExpandButton';
import { TextWithEllipsisFileExtension } from '../../common/TextWithEllipsis';
import Store from '../../store/Store';
import { COLLECTION_ID, DRAG_ITEMS, PWD_COLLECTIONS, SELECTED_COLLECTION } from '../../store/Constants';
import Spinner from '../../common/Spinner';

import System from '../../basic/System';
import { ThemeSetup } from '../../themes/ThemeSetup';
import { isMobile } from '../../util/ScreenUtil';
import { toast } from '../../store/StoreUtil';
import t from '../../i18n/I18n';
import ViewUtil from '../../ViewUtil';
import CollectionUtil from '../../queries/lightbox/CollectionUtil';
import StoreListener from '../../store/StoreListener';
const debug = require('../../debug/debug')('wc:common:CollectionsTreeView');

const COLLECTIONSVIEW = 'COLLECTIONSVIEW';

export class CollectionTreeNode {
  _parent: ?CollectionTreeNode;
  _children: CollectionTreeNode[];
  _fetchChildren: ?Function;
  _loading: ?boolean;
  _data: ZmCollection;
  _hide: boolean;
  _isChildLoaded: boolean;

  constructor(data: ZmCollection, parent: ?CollectionTreeNode) {
    this._data = data;
    this._parent = parent;
    this._hide = false;
  }

  setHide(hide: boolean): void {
    this._hide = hide;
  }

  isHide(): boolean {
    return this._hide;
  }

  getCollectionId(): number {
    return this._data.getCollectionId();
  }

  getCollection(): ZmCollection {
    return this._data;
  }

  getName(): string {
    return this._data.getName();
  }

  getParent(): ?CollectionTreeNode {
    return this._parent;
  }

  getChildren(): CollectionTreeNode[] {
    return this._children ? this._children : [];
  }

  getChildrenSize(): number {
    return _.size(this._children);
  }

  isChildLoaded(): boolean {
    return this._isChildLoaded;
  }

  hasChildren(): boolean {
    return _.size(this._children) > 0;
  }

  hasNoChild(): boolean {
    return _.size(this._children) === 0;
  }

  getChildAt(index: number): ?CollectionTreeNode {
    if (_.size(this._children) > index) {
      return _.nth(this._children, index);
    }
    return null;
  }

  setChildrenFromCollection(collectionList: ZmCollection[]): void {
    const kids: CollectionTreeNode[] = collectionList.map((r: ZmCollection) => {
      return new CollectionTreeNode(r, this);
    });
    this._isChildLoaded = true;
    this._children = kids;
  }

  async remoteFetch(): Promise<CollectionTreeNode> {
    return new Promise(async (onSuccess, onFailure) => {
      if (this.getCollectionId() == CollectionUtil.ROOT_COLLECTION_ID || this.isBinCollection()) {
        this._loading = true;
        try {
          let prevChilds = this.getChildren();
          let collectionList = [];
          let response = await CollectionUtil.listCollections(this.getCollectionId() <= 0 ? CollectionUtil.ROOT_COLLECTION_ID : this.getCollectionId(),
            true, CollectionUtil.COLLECTION_SORT_CRITERIAS.NAME);
          //Note: check why store's id was set here.
          //Store.put(COLLECTION_ID, this.getCollectionId() <= 0 ? CollectionUtil.ROOT_COLLECTION_ID : this.getCollectionId());
          if (this.getCollectionId() <= 0) {
            for (const coll of response.getCollections()) {
              if (coll && coll.getParentId() <= 0) {
                collectionList.push(coll);
              }
            }
          } else {
            let collection = response.getCollections() ? response.getCollections()[0] : null;
            if (collection != null && CollectionUtil.isBinCollection(collection)) {
              for (const childCollectionId of collection.getChildCollections()) {
                let childCollection = response.getCollections().find(c => c.getCollectionId() === childCollectionId);
                if (!childCollection) {
                  let childResponse = await CollectionUtil.listCollections(childCollectionId, false, CollectionUtil.COLLECTION_SORT_CRITERIAS.NAME);
                  childCollection = childResponse.getCollections() ? childResponse.getCollections()[0] : null;
                }
                collectionList.push(childCollection);
              }
            }
          }
          this.setChildrenFromCollection(collectionList);
          this._loading = false;
          onSuccess();
        } catch (err) {
          debug('[componentDidMount] error: ', err);
          this.setChildrenFromCollection([]);
          this._loading = false;
        }
      }
    });
  }

  isLoading(): boolean {
    return this._loading;
  }

  isBinCollection(): boolean {
    return CollectionUtil.isBinCollection(this._data);
  }

}

type Props = {
  namespace: string,
  root: CollectionTreeNode,
  onSelect: Function,
  getIcon: Function,
  getLabel: Function,
  getIsLeaf: Function,
  // decorate?: Function,
  isIndependentTreeIns: Boolean
};

type State = {
  activeNode: ?CollectionTreeNode,
  expandedNodes: Object
};

const storeItems = [PWD_COLLECTIONS];
@StoreListener(storeItems)
export class CollectionsTreeView extends React.Component<Props, State> {
  CollectionsViewNode: React.Component<Props, State>;
  activeContext: ZmCollection;
  activeElement: any;
  treeNodeRef: HTMLElement;
  // _assetListBlockRef: any = { onDeleteSuccess: _.noop, onRenameSuccess: _.noop, downloadSingleAsset: _.noop };
  // _haveAssetListRef: boolean = false;
  constructor(props: Props) {
    super(props);
    // this.CollectionsViewNode = props.decorate ? props.decorate(CollectionsViewNode) : CollectionsViewNode;
    this.CollectionsViewNode = CollectionsViewNode;
    this.state = Store.get(`COLLECTIONSVIEW.${this.props.namespace}`) || {
      activeNode: this.props.root,
      expandedNodes: { [this.props.root.getCollectionId()]: true },
      showContextMenu: false,
      count: 0
    };
  }

  componentDidMount() {
    this.addListeners();
    this.init();
    window.gs.onParentSelection = this.onParentSelection;
  }

  componentWillUnmount() {
    this.removeListeners();
    Store.put(`COLLECTIONSVIEW.${this.props.namespace}`, { ...this.state });
    window.gs.onParentSelection = null;
  }

  triggerUpdate = () => {
    const { count } = this.state;
    this.setState({ count: count + 1 });
  };

  onRef = (node) => {
    this.treeNodeRef = node;
  };

  onContextMenuClick = (e, node) => {
    e.preventDefault();
    if (this.props.isIndependentTreeIns) return;
    if (this.state.activeNode.getCollectionId() !== node.getCollectionId()) this.onClick(e, node, false);
    if (node.getCollectionId() <= 0) {
      this.activeContext = node.getCollection();
      const x = e.clientX;
      const y = e.clientY;
      this.setState({
        showContextMenu: {
          screenX: x,
          screenY: y
        }
      });
      if (this.treeNodeRef) {
        this.activeElement = this.treeNodeRef.querySelector('[collectionId="' + node.getCollectionId() + '"]');
        this.activeElement.style = 'background:var(--primary-elements-bg-hover);';
      }
    }
  };

  onParentSelection = () => {
    const { activeNode } = this.state;
    this.onClick(null, activeNode.getParent(), false);
  };

  closeContextMenu = () => {
    if (this.activeElement) {
      this.activeElement.style = '';
    }
    this.setState({ showContextMenu: false });
  };

  init = () => {
    const { root } = this.props;
    let expandedNodes = { ...this.state.expandedNodes };

    // Check all expandedNodes to make sure their children have been fetched. Also
    // collapse the ones that don't have children.
    const visit = async (node: CollectionTreeNode) => {
      if (!expandedNodes[node.getCollectionId()]) {
        return;
      }

      if ((node.getCollectionId() <= 0 || node.isBinCollection()) && !node.isChildLoaded()) {
        node.remoteFetch();
        if (node.hasChildren()) {
          node.getChildren().forEach(visit);
        } else {
          debug('[init()] [info] node is expanded but has no children. collapsing', node);
          delete expandedNodes[node.getCollectionId()];
        }
      } else {
        debug('[init()] [warn] node is expanded but does not have a fetchChildren function', node);
        delete expandedNodes[node.getCollectionId()];
        return;
      }

    };
    if (root.hasChildren()) {
      root.getChildren().forEach(visit);
    }

    this.setState({ expandedNodes: _.clone(expandedNodes) });
  };

  addListeners = () => {
    KeyboardShortcuts.add(
      new KeydownConfig(COLLECTIONSVIEW, this.onKeyDown, [
        KeyCode.UP,
        KeyCode.DOWN,
        KeyCode.LEFT,
        KeyCode.RIGHT,
        KeyCode.ENTER
      ])
    );
  };

  removeListeners = () => {
    KeyboardShortcuts.remove(COLLECTIONSVIEW);
  };

  updateActiveNodeAndExpandedNodes = (activeNode: CollectionTreeNode, expandedNodeIds: number[]) => {
    let expandedNodes = { ...this.state.expandedNodes };
    expandedNodes[activeNode.getCollectionId()] = true;
    _.map(expandedNodeIds, (id) => (expandedNodes[id] = true));
    this.setState({ activeNode, expandedNodes: _.clone(expandedNodes) });
    this.scrollToPosition(activeNode.getCollectionId());
  };

  onUpArrowKeyDown = () => {
    const { activeNode } = this.state;
    if (!activeNode) {
      return;
    }

    const parent = activeNode.getParent();
    if (parent) {
      const index = parent.getChildren().indexOf(activeNode);
      if (index === -1) {
        return;
      }

      if (index > 0) {
        // Select previous sibling
        this.putActiveNodeIntoScrollView(parent.getChildAt(index - 1));
        this.setState({ activeNode: parent.getChildAt(index - 1) });
        return;
      } else {
        // Select parent
        this.putActiveNodeIntoScrollView(parent);
        this.setState({ activeNode: parent });
        return;
      }
    }
  };

  putActiveNodeIntoScrollView = (activeNode) => {
    const activeNode_Dom = document.querySelector(`[drop-fuid="${activeNode?.getCollectionId()}"]`);
    return activeNode_Dom ? activeNode_Dom.scrollIntoView(false) : null;
  }

  onDownArrowKeyDown = () => {
    const { activeNode, expandedNodes } = this.state;
    if (!activeNode) {
      return;
    }

    // Try to select first child
    if (expandedNodes[activeNode.getCollectionId()] && activeNode.hasChildren()) {
      this.putActiveNodeIntoScrollView(activeNode.getChildAt(0));
      this.setState({ activeNode: activeNode.getChildAt(0) });
      return;
    }

    // Try to select next sibling
    let parent = activeNode.getParent();
    if (parent) {
      const children = parent.getChildren();
      const index = children.indexOf(activeNode);
      this.putActiveNodeIntoScrollView(children[index + 1])
      if (index > -1 && index < children.length - 1) {
        this.setState({ activeNode: children[index + 1] });
        return;
      }
    }

    // Try to select next parent
    let grandparent = parent ? parent.getParent() : null;
    while (grandparent) {
      if (grandparent.hasNoChild()) {
        break;
      }
      const uncles = grandparent.getChildren();
      const index = uncles.indexOf(parent);
      if (index === -1) {
        break;
      }

      if (index === uncles.length - 1) {
        parent = grandparent;
        grandparent = grandparent.getParent();
        continue;
      }

      this.putActiveNodeIntoScrollView(uncles[index + 1]);
      this.setState({ activeNode: uncles[index + 1] });
      return;
    }
  };

  onLeftArrowKeyDown = () => {
    let expandedNodes = { ...this.state.expandedNodes };
    const { activeNode } = this.state;
    const parent: ?CollectionTreeNode = activeNode ? activeNode.getParent() : null;
    if (activeNode && expandedNodes[activeNode.getCollectionId()]) {
      delete expandedNodes[activeNode.getCollectionId()];
      this.setState({ expandedNodes: _.clone(expandedNodes) });
      return;
    } else if (parent && parent.isHide()) {
      this.setState({ activeNode: parent });
    }
  };

  onRightArrowKeyDown = async () => {
    let expandedNodes = { ...this.state.expandedNodes };
    const { getIsLeaf } = this.props;
    const { activeNode } = this.state;

    if (activeNode) {
      const id = activeNode.getCollectionId();
      if (!expandedNodes[id] && !getIsLeaf(activeNode)) {
        expandedNodes[id] = true;
        activeNode.remoteFetch().then(() => {
          if (activeNode.hasChildren()) {
            activeNode.getChildren().forEach((n) => (expandedNodes[n.getCollectionId()] = false));
          }
          this.setState({ expandedNodes: _.clone(expandedNodes) });
        });

      }
    }
  };

  onEnterKeyDown = () => {
    if (ViewUtil.isBrowseSubTabActive())
      this.props.onSelect(this.state.activeNode);
  };

  onKeyDown = (e: KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    switch (e.keyCode) {
      case KeyCode.UP:
        return this.onUpArrowKeyDown();
      case KeyCode.DOWN:
        return this.onDownArrowKeyDown();
      case KeyCode.LEFT:
        return this.onLeftArrowKeyDown();
      case KeyCode.RIGHT:
        return this.onRightArrowKeyDown();
      case KeyCode.ENTER:
        return this.onEnterKeyDown();
    }
  };

  scrollToPosition(collectionId) {
    const treeElement = document.getElementsByName('CollectionTreeNode')[0];
    if (treeElement) {
      const element = treeElement.querySelector(`[collctionId="${collectionId}"]`);
      if (element) {
        if (this.props.isIndependentTreeIns)
          element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        else element.scrollIntoView();
      }
    }
  }

  onClick = async (e: MouseEvent, node: CollectionTreeNode, expandOnly: boolean) => {
    e ? e.stopPropagation() : null;
    const { getIsLeaf, onSelect } = this.props;
    const expandedNodes = { ...this.state.expandedNodes };
    const id = node.getCollectionId();

    if (!getIsLeaf(node)) {
      if (expandedNodes[id]) {
        if (expandOnly) {
          delete expandedNodes[id];
        }
      } else {
        if (node.getCollectionId() <= 0 || node.isBinCollection()) {
          this.triggerUpdate();
          await node.remoteFetch().then(() => {
            expandedNodes[id] = true;
            node.getChildren().forEach((n) => (expandedNodes[n.getCollectionId()] = false));
          }, () => {
            this._loading = false;
          });
        }
      }

      if (expandOnly) {
        debug('[onClick()] expandOnly=true', { expandedNodes });
        this.setState({ expandedNodes: _.clone(expandedNodes) });
      } else {
        debug('[onClick()] expandOnly=false', { expandedNodes, activeNode: node });
        this.setState({ expandedNodes: _.clone(expandedNodes), activeNode: node });
        onSelect(node);
      }
    } else {
      debug('[onClick()] selecting collection: ', node);
      this.setState({ activeNode: node });
      onSelect(node);
    }
    this.scrollToPosition(node.getCollectionId());
  };

  //Note : Not in use.
  disableAllFileView = () => {
    window.AbortSignal.activeSubTab = SUBTAB.BROWSE.KEY;
  };

  renderNode = (node: CollectionTreeNode, depth: number) => {
    const pwd_collections = ViewUtil.getCollectionPwd();
    const CollectionsViewNode = this.CollectionsViewNode;
    const { getIcon, getLabel, getIsLeaf } = this.props;
    const { activeNode, expandedNodes } = this.state;
    const id = node.getCollectionId();
    const showExpanded = !!expandedNodes[id] || (pwd_collections && pwd_collections.includes(id));

    const props: CollectionsViewNodeProps = {
      key: id,
      node,
      isActive: pwd_collections && pwd_collections.length > 0 ? pwd_collections[pwd_collections.length - 1] === node.getCollectionId() : ViewUtil.isCollectionBrowseTabActive() ? (activeNode ? activeNode.getCollectionId() === node.getCollectionId() : false) : false,
      isExpanded: showExpanded,
      isLeaf: getIsLeaf(node),
      depth,
      onClick: this.onClick,
      getIcon,
      getLabel,
      onContextMenuClick: this.onContextMenuClick,
      renderChildren: (parent: CollectionTreeNode, depth: number) => {
        if ((parent.getChildren() && parent.getChildren().length === 0) && parent.isBinCollection() && !parent.isChildLoaded())
          parent.remoteFetch();
        const children = parent.getChildren().map((node: CollectionTreeNode) => this.renderNode(node, depth));
        //$FlowFixMe
        return <div className="children">{children}</div>;
      }
    };

    return <CollectionsViewNode {...props} />;
  };

  render() {
    const { root } = this.props;
    const { showContextMenu } = this.state;
    let treeHeight =
      document.querySelector('[name=AllCollectionsTree]') && document.querySelector('.folder') && document.querySelector('[name=AllAndMine]')
        ? window.innerHeight -
        (70 + 15 +
          30 + 30 +
          document.querySelector('[name=AllCollectionsTree]').offsetHeight + document.querySelector('[name=AllAndMine]').offsetHeight +
          document.querySelector('.folder').offsetHeight)
        : null;
    treeHeight = isMobile() ? treeHeight + 20 : treeHeight;

    return (
      <React.Fragment>
        <div
          name="CollectionsTreeView"
          ref={this.onRef}
          style={treeHeight ? { height: treeHeight + 'px', overflow: 'auto' } : null}>
          {this.renderNode(root, 0)}
          {
            root.isHide() ?
              <div>
                {root.getChildren().map((n) => this.renderNode(n, 0))}
              </div> : null
          }
        </div>
      </React.Fragment >
    );
  }
}

type CollectionsViewNodeProps = {
  node: CollectionTreeNode,
  isActive: boolean,
  isExpanded: boolean,
  isLeaf: boolean,
  depth: number,
  onClick: Function,
  getIcon: Function,
  getLabel: Function,
  renderChildren: Function,
  isOver?: boolean,
  connectDropTarget?: Function
};

function CollectionsViewNode(props: CollectionsViewNodeProps) {
  const {
    node,
    isActive,
    isExpanded,
    isLeaf,
    depth,
    onClick,
    getIcon,
    getLabel,
    renderChildren,
    isOver,
    connectDropTarget,
    onContextMenuClick
  } = props;

  const paddingLeft = depth * 12 + 2;
  const maxWidth = 240;

  const infoStyle = { paddingLeft };
  const icon = isActive
    ? 'images/arrow_active.svg'
    : isExpanded
      ? ThemeSetup.getImageURL('arrow_expanded.svg')
      : ThemeSetup.getImageURL('arrow.svg');

  const handleDragHover = (e, collection) => {
    if (System.isDesktop()) {
      if (_.isEmpty(Store.get(DRAG_ITEMS))) {
        toast(t('UPLOAD_TO') + collection.getName());
      }
    }
    if ('dragover' == e.type) {
      window.gs.dropDir = collection;
      e.currentTarget.style.opacity = 0.5;
      e.currentTarget.classList.add('dropActive');
    } else {
      window.gs.dropDir = null;
      e.currentTarget.style.opacity = 1;
      e.currentTarget.classList.remove('dropActive');
    }
  };

  const onDropHandle = (e, asset) => {
    // if (System.isDesktop()) {
    e.currentTarget.style.opacity = 1;
    e.currentTarget.classList.remove('dropActive');
    // }
  };
  const content = (
    <div
      className={classNames('info', { 'drag-hover': isOver && !isLeaf })}
      onDragOver={(e) => handleDragHover(e, node.getCollection())}
      onDragLeave={(e) => handleDragHover(e, node.getCollection())}
      onDrop={onDropHandle}
      onContextMenu={(e) => onContextMenuClick(e, node)}
      onClick={(e) => onClick(e, node, false)}
      drop-fuid={node.getCollection().getCollectionId()}
      style={infoStyle}>
      <ExpandButton isExpanded={isExpanded} onClick={(e) => onClick(e, node, true)} icon={icon} visibility={isLeaf} />
      {node.isLoading() ? <Spinner small /> : <img src={getIcon(node, isExpanded, isActive)} />}
      <label
        className={classNames('label', isExpanded && !isActive ? 'expanded' : '', isActive ? 'labelIsActive' : 'labelInActive')}>
        <TextWithEllipsisFileExtension
          text={getLabel(node)}
          title={getLabel(node)}
          textStyle={{ fontSize: 14, maxWidth: 'calc(100%-5px)' }}
          responsive={false}
        />
      </label>
    </div>
  );

  return (
    <div
      name="CollectionsViewNode"
      fuid={node.getCollection().getCollectionId()}
      treefuid="true"
      className={classNames({ active: isActive, firstNode: node.getCollection().getCollectionId() <= 0 })}>
      {connectDropTarget ? connectDropTarget(content) : content}
      {!isLeaf && isExpanded ? renderChildren(node, depth + 1) : null}
    </div>
  );
}

export default CollectionsTreeView;